// src/pages/ProtocolReviewPage.js
//
// Phase 9 — Human review of an extracted SOA: page image on the left, the
// editable structured table on the right, low-confidence cells highlighted.
// Route: /sourcex/protocols/:studyId/:protocolId (flag-gated in App.js).
//
// Reviewer edits visit headers, assessment names/scope, cell markers/mode, and
// footnotes, then Approves — which (server-side) freezes an approved snapshot
// and generates the per-visit source docs the AI is allowed to use.

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getDocs, collection, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { getProtocolPagesPath, getSourcexTemplatesPath } from "../services/firestorePaths";
import {
  listenExtraction,
  saveExtractionEdits,
  approveExtraction,
  getProtocol,
} from "../services/protocolExtractionService";
import {
  LOW_CONFIDENCE_THRESHOLD,
  VISIT_TYPES,
  APPLIES_TO,
  emptyExtraction,
  buildTemplateSectionsFromSoa,
} from "../services/protocolTypes";
import { prepareTemplateForWrite } from "../services/templateSchema";
import { PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { listenVersions } from "../services/protocolVersionService";
import { protocolRole } from "../services/protocolVersions";
import { useSiteAdmin } from "../components/sourcex/useSiteAdmin";
import AiHelperNotice from "../components/AiHelperNotice";
import { logProtocolEvent } from "../services/protocolVersionService";
import { useDialog } from "../components/sourcex/useDialog";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Phone,
  FileText,
} from "lucide-react";

// Click-cycle for a cell: blank -> in-person X -> phone -> blank.
const cycleCell = (cell) => {
  const marker = cell?.marker || "blank";
  if (marker === "blank") return { marker: "X", required: true, mode: "in_person", footnote_refs: cell?.footnote_refs || [], confidence: 1 };
  if (marker === "X" || marker === "check" || marker === "other")
    return { marker: "phone_icon", required: true, mode: "phone", footnote_refs: cell?.footnote_refs || [], confidence: 1 };
  return { marker: "blank", required: false, mode: "unknown", footnote_refs: cell?.footnote_refs || [], confidence: 1 };
};

const isLow = (c) => typeof c === "number" && c < LOW_CONFIDENCE_THRESHOLD;

// Column tooltip. Anything the extraction learned from a FOOTNOTE rather than
// from the grid itself is shown with the sentence it came from — otherwise a
// visit that became a phone visit because of footnote (c) looks like the reader
// simply decided so, and a reviewer has no way to check it.
const visitTitle = (v) => {
  const parts = [v.visit_label || v.visit_id];
  Object.entries(v.attributes || {}).forEach(([k, a]) => {
    if (!a || !a.value) return;
    parts.push(`${k}: ${a.value}${a.evidence ? ` — "${a.evidence}"` : ""}`);
  });
  return parts.join("\n");
};

export default function ProtocolReviewPage() {
  const isSiteAdmin = useSiteAdmin();
  const { studyId, protocolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // This page is reachable from two places: SourceX's own Protocols list, and
  // the Screening Visit card on the study page. Back should return whoever
  // opened it to where they were, not always to SourceX.
  const cameFromStudy =
    location.state?.from === "study" && Boolean(location.state?.studyId);
  const backLabel = cameFromStudy ? "Study" : "Protocols";
  const goBack = () =>
    navigate(
      cameFromStudy
        ? `/study/${location.state.studyId}`
        : `/sourcex/protocols/${studyId}`
    );

  const [protocol, setProtocol] = useState(null);
  const [ext, setExt] = useState(null); // working extraction (editable)
  const [pageUrls, setPageUrls] = useState({}); // pageNumber -> url
  const [activePage, setActivePage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const { confirm: askConfirm, dialog } = useDialog();

  const approved = protocol?.status === "approved";
  // Protocol versions: an older version can't be approved while no source
  // documents are built (Version N is the one to work on).
  const [versions, setVersions] = useState([]);
  const [docsBuilt, setDocsBuilt] = useState(null);
  useEffect(() => {
    if (!PROTOCOL_COMPARE_ENABLED || !studyId) return undefined;
    getDoc(doc(db, getSourcexTemplatesPath(), `${studyId}__assembled`))
      .then((snap) => setDocsBuilt(snap.exists()))
      .catch(() => setDocsBuilt(false));
    return listenVersions(studyId, setVersions, () => setVersions([]));
  }, [studyId]);
  const role = PROTOCOL_COMPARE_ENABLED && protocol ? protocolRole({ id: protocolId, ...protocol }, versions, { docsBuilt: !!docsBuilt }) : null;
  // Only once we KNOW no documents are built (docsBuilt false, not still loading).
  const lockedOlder = !!(role && role.locked && docsBuilt === false);
  const currentNumber = versions.reduce((n, v) => Math.max(n, Number(v.version) || 0), 0);

  // Live extraction.
  useEffect(() => {
    if (!studyId || !protocolId) return undefined;
    const unsub = listenExtraction(
      studyId,
      protocolId,
      (data) => setExt(data ? { ...emptyExtraction(), ...data } : null),
      (e) => setError(e.message || "Failed to load extraction.")
    );
    return () => unsub && unsub();
  }, [studyId, protocolId]);

  // Protocol meta.
  useEffect(() => {
    getProtocol(studyId, protocolId).then(setProtocol).catch(() => {});
  }, [studyId, protocolId]);

  // Resolve page-image download URLs (access-gated; not public).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, getProtocolPagesPath(studyId, protocolId)));
        const entries = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data();
            try {
              const url = await getDownloadURL(ref(storage, data.storagePath));
              return [data.pageNumber, url];
            } catch {
              return [data.pageNumber, null];
            }
          })
        );
        if (cancelled) return;
        const map = Object.fromEntries(entries.filter(([, u]) => u));
        setPageUrls(map);
        const first = Object.keys(map).map(Number).sort((a, b) => a - b)[0];
        setActivePage((p) => p ?? first ?? null);
      } catch {
        /* page images are best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId, protocolId]);

  const visits = ext?.visits || [];
  const assessments = ext?.assessments || [];
  const footnotes = ext?.footnotes || [];
  const tables = ext?.tables || [];
  // Per-cell agreement between the page read and the high-resolution tile read.
  const tileAudit = ext?.tileAudit || null;
  // Outcome of the third pass that settles cells the two grid reads disputed.
  const adjudication = ext?.adjudication || null;
  // Only a protocol with more than one schedule needs the extra header row and
  // the banner; a single-table review looks exactly as it always has.
  const multiTable = tables.length > 1;

  // Structural findings the reviewer must see BEFORE trusting the grid —
  // multiple schedules, a refused visit merge, a footnote that contradicts the
  // table. These used to sit in the data where nobody read them.
  const STRUCTURAL = [
    "multiple_soa_tables",
    "visit_reconcile_conflict",
    "modality_conflict",
    "enrichment_truncated",
    "tile_row_undercount",
    "tile_pass_incomplete",
    "tile_marks_unconfirmed",
  ];
  const structuralWarnings = (ext?.warnings || []).filter((w) => STRUCTURAL.includes(w.type));

  // ONE GRID PER SCHEDULE.
  //
  // A protocol with two schedules used to render as a single grid with every
  // table's visits side by side and a spanning header naming each run. That is
  // fine for the machine and hostile for the human doing the checking: 50-odd
  // columns, two numbering schemes interleaved, and no way to work through one
  // schedule at a time against the PDF.
  //
  // Every assessment row appears in EVERY table, deliberately. Hiding a row
  // because it has no marks in this schedule would hide exactly the case this
  // review exists to catch — a mark the extraction missed. The reviewer must be
  // able to add one, so the row has to be on screen.
  const tableViews = useMemo(() => {
    if (!multiTable) return [{ key: "all", table: null, visits }];
    return tables
      .map((t) => ({
        key: t.table_id,
        table: t,
        visits: visits.filter((v) => v.table_id === t.table_id),
      }))
      .filter((v) => v.visits.length);
  }, [multiTable, visits, tables]);

  const lowCount = useMemo(() => {
    let n = 0;
    visits.forEach((v) => isLow(v.confidence) && (n += 1));
    assessments.forEach((a) =>
      (a.cells || []).forEach((c) => c.marker !== "blank" && isLow(c.confidence) && (n += 1))
    );
    return n;
  }, [visits, assessments]);

  // --- mutators (local working copy) ---
  const patch = (next) => setExt((e) => ({ ...e, ...next }));
  const setVisit = (i, field, value) =>
    patch({ visits: visits.map((v, j) => (j === i ? { ...v, [field]: value } : v)) });
  const setAssessment = (i, field, value) =>
    patch({ assessments: assessments.map((a, j) => (j === i ? { ...a, [field]: value } : a)) });
  const toggleCell = (ai, visitId) => {
    if (approved) return;
    patch({
      assessments: assessments.map((a, j) => {
        if (j !== ai) return a;
        const cells = (a.cells || []).filter((c) => c.marker !== "blank");
        const existing = cells.find((c) => c.visit_id === visitId);
        const updated = cycleCell(existing);
        let nextCells;
        if (updated.marker === "blank") nextCells = cells.filter((c) => c.visit_id !== visitId);
        else if (existing)
          nextCells = cells.map((c) => (c.visit_id === visitId ? { ...updated, visit_id: visitId } : c));
        else nextCells = [...cells, { ...updated, visit_id: visitId }];
        return { ...a, cells: nextCells };
      }),
    });
  };
  const setFootnote = (i, field, value) =>
    patch({ footnotes: footnotes.map((f, j) => (j === i ? { ...f, [field]: value } : f)) });

  const cellFor = (a, visitId) => (a.cells || []).find((c) => c.visit_id === visitId && c.marker !== "blank");

  const handleSave = async () => {
    setError("");
    setMsg("");
    setSaving(true);
    try {
      await saveExtractionEdits(studyId, protocolId, ext);
      setMsg("Saved.");
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setError("");
    setMsg("");
    const ok = await askConfirm(
      "It becomes the AI's source of truth and generates per-visit source docs.",
      { title: "Approve this extraction?", confirmLabel: "Approve" }
    );
    if (!ok) return;
    setApproving(true);
    try {
      await saveExtractionEdits(studyId, protocolId, ext); // persist pending edits first
      const r = await approveExtraction(studyId, protocolId);
      setMsg(`Approved — ${r.visitDocs || 0} visit source docs generated.`);
      setProtocol((p) => ({ ...(p || {}), status: "approved" }));
    } catch (e) {
      setError(e.message || "Approval failed.");
    } finally {
      setApproving(false);
    }
  };

  // Turn this SOA into the SourceX source-document template (Questions per
  // visit), then hand off to the existing SectionEditor → AddPatient →
  // PatientSource workflow. Saved as a DRAFT so it is reviewed/approved there.
  const handleBuildTemplate = async () => {
    setError("");
    setMsg("");
    // Source documents already built: greyed out for staff; a site admin can
    // override after a clear warning, and it is recorded.
    if (PROTOCOL_COMPARE_ENABLED && docsBuilt) {
      if (!isSiteAdmin) return;
      const ok = await askConfirm(
        "Source documents for this study are already built. Edit those instead of building a new template. Build a template from this protocol anyway?",
        { title: "Source documents are already built", confirmLabel: "Build anyway (admin)", tone: "danger" }
      );
      if (!ok) return;
      logProtocolEvent(studyId, { type: "rebuild_override", protocolId, detail: "Protocol review: Build Source Template" });
    }
    const sections = buildTemplateSectionsFromSoa(ext);
    if (!sections.length) {
      setError("No visits/assessments to build a source template from.");
      return;
    }
    try {
      const tref = doc(db, getSourcexTemplatesPath(), studyId);
      const existing = await getDoc(tref);
      if (
        existing.exists() &&
        (existing.data().sections || []).length > 0 &&
        existing.data().status === "approved"
      ) {
        const ok = await askConfirm(
          "Replace it with a new draft built from this SOA? You will review and approve it again.",
          {
            title: "This study already has an APPROVED source template",
            confirmLabel: "Replace it",
            tone: "danger",
          }
        );
        if (!ok) return;
      }
      // Same funnel as the other AI draft path: stable ids + no answers.
      const clean = prepareTemplateForWrite({ sections });
      await setDoc(
        tref,
        {
          studyId,
          sections: clean.sections,
          schemaVersion: clean.schemaVersion,
          headerFields: clean.headerFields,
          status: "draft",
          aiGenerated: true,
          source: "soa_extraction",
          sourceProtocolId: protocolId,
          aiProvenanceNote: `Built from SOA extraction${
            ext.source_pages?.length ? ` (pp. ${ext.source_pages.join(", ")})` : ""
          }.`,
          aiGeneratedAt: serverTimestamp(),
        },
        { merge: true }
      );
      navigate(`/sourcex/template/${studyId}`);
    } catch (e) {
      setError(e.message || "Could not build source template.");
    }
  };

  return (
    <div className="sourcex-page min-h-screen bg-[#0b0820] text-slate-100 font-[Manrope] px-4 py-5">
      {/* Centered alert / confirm for this page. */}
      {dialog}
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={goBack}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
            </button>
            <h1 className="text-base font-semibold truncate">
              Review SOA · {protocol?.fileName || protocolId}
            </h1>
            {approved && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border bg-emerald-500/15 text-emerald-200 border-emerald-400/30">
                <CheckCircle2 className="w-3 h-3" /> approved
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {lowCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5" /> {lowCount} low-confidence
              </span>
            )}
            <button
              onClick={handleBuildTemplate}
              disabled={!ext || visits.length === 0 || (PROTOCOL_COMPARE_ENABLED && !!docsBuilt && !isSiteAdmin)}
              title={
                PROTOCOL_COMPARE_ENABLED && docsBuilt
                  ? "Source documents are already built, so edit those instead."
                  : "Generate the per-visit source-document template from this SOA"
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] text-xs font-medium text-slate-100 disabled:opacity-40 transition"
            >
              <FileText className="w-4 h-4 text-blue-300" /> Build Source Template
            </button>
            {!approved && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || !ext}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] text-xs font-medium text-slate-100 disabled:opacity-40 transition"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving || !ext || visits.length === 0 || lockedOlder}
                  title={lockedOlder ? `Version ${currentNumber} is the current version` : undefined}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-xs font-semibold text-white hover:brightness-110 disabled:opacity-40 transition"
                >
                  {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve
                </button>
              </>
            )}
          </div>
        </div>

        {PROTOCOL_COMPARE_ENABLED && (
          <AiHelperNotice className="mb-3">This schedule of assessments was read from the protocol by AI. Check it against the protocol before approving.</AiHelperNotice>
        )}
        {lockedOlder && (
          <div className="mb-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-slate-300">
            This is Version {role.version || 1}, an older version. Version {currentNumber} is the current one, so it can&apos;t be approved here.
          </div>
        )}
        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
        {msg && (
          <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
            {msg}
          </div>
        )}

        {structuralWarnings.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[13px] text-amber-100">
            <div className="flex items-center gap-2 font-medium mb-1">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Check these before approving
            </div>
            <ul className="list-disc pl-5 space-y-0.5 text-amber-100/90">
              {structuralWarnings.map((w, i) => (
                <li key={i}>
                  {w.message}
                  {w.source_page ? (
                    <span className="text-amber-200/60"> (p.{w.source_page})</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {multiTable && (
          <div className="mb-3 rounded-lg border border-blue-500/25 bg-blue-500/[0.07] px-3 py-2 text-[13px] text-blue-100">
            <span className="font-medium">{tables.length} schedules in this protocol.</span>{" "}
            Visits are scoped to their own schedule and are never merged across them.
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tables.map((t) => (
                <span
                  key={t.table_id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/10 text-[11px] text-blue-100/90"
                >
                  {t.label}
                  {t.population ? (
                    <span className="text-blue-200/60">· {t.population}</span>
                  ) : null}
                  <span className="text-blue-200/50">p.{(t.pages || []).join(", ")}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {!ext ? (
          <div className="py-16 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading extraction…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT — page image */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                {Object.keys(pageUrls)
                  .map(Number)
                  .sort((a, b) => a - b)
                  .map((p) => (
                    <button
                      key={p}
                      onClick={() => setActivePage(p)}
                      className={`px-2.5 py-1 rounded-md text-xs border transition ${
                        activePage === p
                          ? "bg-purple-500/20 border-purple-400/40 text-white"
                          : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08]"
                      }`}
                    >
                      p.{p}
                    </button>
                  ))}
              </div>
              <div className="rounded-lg overflow-auto max-h-[78vh] bg-black/30 flex items-start justify-center">
                {activePage && pageUrls[activePage] ? (
                  <img
                    src={pageUrls[activePage]}
                    alt={`SOA page ${activePage}`}
                    className="max-w-full"
                  />
                ) : (
                  <div className="py-16 text-sm text-slate-500">
                    Page image unavailable.
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT — editable structured table */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3 space-y-4 max-h-[82vh] overflow-auto">
              {/* Visits */}
              <section>
                <h3 className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Visits ({visits.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {visits.map((v, i) => (
                    <div
                      key={v.visit_id || i}
                      className={`rounded-lg p-2 border ${
                        isLow(v.confidence)
                          ? "border-amber-400/50 bg-amber-400/5"
                          : "border-white/10 bg-white/[0.02]"
                      }`}
                    >
                      <input
                        value={v.visit_label || ""}
                        disabled={approved}
                        onChange={(e) => setVisit(i, "visit_label", e.target.value)}
                        placeholder="Visit label"
                        className="w-full bg-transparent text-sm font-medium text-white outline-none"
                      />
                      <div className="mt-1 grid grid-cols-3 gap-1">
                        {["week", "day", "time_window"].map((f) => (
                          <input
                            key={f}
                            value={v[f] || ""}
                            disabled={approved}
                            onChange={(e) => setVisit(i, f, e.target.value)}
                            placeholder={f === "time_window" ? "window" : f}
                            className="bg-white/[0.04] rounded px-1.5 py-0.5 text-[11px] text-slate-200 outline-none"
                          />
                        ))}
                      </div>
                      <select
                        value={v.visit_type || "unknown"}
                        disabled={approved}
                        onChange={(e) => setVisit(i, "visit_type", e.target.value)}
                        className="mt-1 w-full bg-white/[0.04] rounded px-1.5 py-0.5 text-[11px] text-slate-200 outline-none"
                      >
                        {VISIT_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-[#0b0820]">
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </section>

              {/* Assessment matrix */}
              <section>
                <h3 className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                  Assessments ({assessments.length})
                  <span className="ml-2 normal-case tracking-normal text-slate-500">
                    — click a cell to cycle blank → X → phone
                  </span>
                </h3>
                {tileAudit && (
                  <div className="flex flex-wrap items-center gap-3 mb-2 text-[11px] text-slate-400">
                    <span className="text-slate-500">Two reads per cell:</span>
                    <span className="inline-flex items-center gap-1">
                      <span className="text-emerald-300 font-semibold">✕</span> confirmed by both
                      <span className="text-slate-500">({tileAudit.confirmed || 0})</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                      recovered by the hi-res pass
                      <span className="text-slate-500">({tileAudit.added || 0})</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-rose-200">
                      <span className="w-2.5 h-2.5 rounded-sm ring-1 ring-rose-400/70 bg-rose-500/20 inline-block" />
                      page read only — check these
                      <span className="text-rose-300/70">({tileAudit.unconfirmed || 0})</span>
                    </span>
                    {adjudication && (
                      <span className="inline-flex items-center gap-1 text-amber-200">
                        <span className="w-2.5 h-2.5 rounded-sm ring-1 ring-amber-300 bg-amber-400/25 inline-block" />
                        disputed, unresolved — you decide
                        <span className="text-amber-300/70">({adjudication.unclear || 0})</span>
                      </span>
                    )}
                  </div>
                )}
                {adjudication && (
                  <p className="mb-2 text-[11px] text-slate-500">
                    A third pass re-checked the {adjudication.asked} cell(s) the two reads
                    disagreed about: {adjudication.kept} confirmed, {adjudication.removed} removed
                    as not actually marked, {adjudication.addedBack} added back,{" "}
                    {adjudication.unclear} left for you.
                  </p>
                )}
                {/* Capping the height is what keeps BOTH scrollbars reachable.
                    Unbounded, the horizontal bar sits at the foot of a 50-row
                    table, so reading the right-hand visits meant scrolling to
                    the bottom first. Bounded, both bars stay on screen wherever
                    you are in the grid.

                    border-separate (not collapse) because a collapsed border is
                    painted by the table, not the cell, and therefore vanishes
                    under a sticky header. */}
                {tableViews.map((view, vi) => (
                <div key={view.key} className={vi > 0 ? "mt-6" : ""}>
                {view.table && (
                  <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
                    <h4 className="text-[13px] font-semibold text-blue-100">
                      {view.table.label}
                    </h4>
                    {view.table.population && (
                      <span className="text-[11px] text-blue-200/60">
                        {view.table.population}
                      </span>
                    )}
                    <span className="text-[11px] text-slate-500">
                      {view.visits.length} visit{view.visits.length === 1 ? "" : "s"}
                      {(view.table.pages || []).length
                        ? ` · p.${view.table.pages.join(", ")}`
                        : ""}
                    </span>
                  </div>
                )}
                <div className="overflow-auto border border-white/10 rounded-lg max-h-[70vh]">
                  <table className="text-[11px] border-separate border-spacing-0">
                    <thead>
                      {/* The header stays put while the body scrolls, so the
                          visit a column belongs to is readable from the last row
                          of the table as easily as the first. */}
                      <tr>
                        <th className="sticky left-0 top-0 z-40 bg-[#0b0820] text-left px-2 py-1 min-w-[12rem] border-b border-white/10">
                          Assessment
                        </th>
                        <th className="sticky top-0 z-30 bg-[#0b0820] px-1 py-1 border-b border-white/10">
                          Scope
                        </th>
                        {view.visits.map((v) => (
                          <th
                            key={v.visit_id}
                            className="sticky top-0 z-30 bg-[#0b0820] px-1 py-1 border-b border-white/10 whitespace-nowrap text-slate-300"
                            title={visitTitle(v)}
                          >
                            {(v.visit_label || v.visit_id).slice(0, 10)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {assessments.map((a, ai) => {
                        // Banding has to be OPAQUE on the sticky first column —
                        // a translucent stripe there would let the scrolling
                        // cells show through it. Hence two solid colours rather
                        // than a white overlay.
                        //
                        // Both of those colours are DARK, and only the unstriped
                        // one has a light-mode override — so in light mode every
                        // other row stayed near-black while the text on it was
                        // turned near-black too. The `soa-row-alt` /
                        // `soa-cell-alt` hooks carry the light-mode banding; see
                        // index.css. Dark mode is unchanged.
                        const striped = ai % 2 === 1;
                        const stickyBg = striped ? "bg-[#120e30]" : "bg-[#0b0820]";
                        return (
                        <tr
                          key={a.assessment_id || ai}
                          className={`${striped ? "soa-row-alt bg-white/[0.028]" : ""} hover:bg-white/[0.06]`}
                        >
                          <td className={`sticky left-0 z-10 ${striped ? "soa-cell-alt " : ""}${stickyBg} px-2 py-1 border-b border-white/5`}>
                            <div className="flex items-center gap-1">
                              <input
                                value={a.name || ""}
                                disabled={approved}
                                onChange={(e) => setAssessment(ai, "name", e.target.value)}
                                className="w-44 bg-transparent text-slate-100 outline-none"
                              />
                              {/* The reader counted more marks in this row than
                                  it went on to list — it stopped partway across.
                                  Whatever it did give is kept; the row is called
                                  out so this one gets read against the PDF. */}
                              {a.tileIncomplete && (
                                <AlertTriangle
                                  className="w-3 h-3 shrink-0 text-amber-300"
                                  title="This row may be missing marks — the reader listed fewer than it counted. Check it against the protocol."
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-1 border-b border-white/5">
                            <select
                              value={a.applies_to || "unknown"}
                              disabled={approved}
                              onChange={(e) => setAssessment(ai, "applies_to", e.target.value)}
                              className="bg-white/[0.04] rounded px-1 py-0.5 text-[10px] text-slate-200 outline-none"
                            >
                              {APPLIES_TO.map((t) => (
                                <option key={t} value={t} className="bg-[#0b0820]">
                                  {t}
                                </option>
                              ))}
                            </select>
                          </td>
                          {view.visits.map((v) => {
                            const c = cellFor(a, v.visit_id);
                            const low = c && isLow(c.confidence);
                            // Two independent reads happen per cell: the
                            // page-level pass and the high-resolution tile pass.
                            // A coordinator scanning a uniform grid has no way to
                            // tell a cell both passes agreed on from one only a
                            // single pass saw — so say which is which, and make
                            // the disagreements the ones that catch the eye.
                            const unconfirmed = c && c.unconfirmedByTile;
                            const recovered = c && c.foundByTile;
                            // A third, closed reading settled the cells the two
                            // grid passes disagreed about. "unclear" means it
                            // could not tell either — the one state that is
                            // genuinely a question for a person.
                            const unclear = c && c.adjudicated === "unclear";
                            return (
                              <td
                                key={v.visit_id}
                                onClick={() => toggleCell(ai, v.visit_id)}
                                title={
                                  !c
                                    ? undefined
                                    : unclear
                                    ? "Disputed, and a closer look could not settle it. Decide this one against the protocol."
                                    : c.adjudicated === "yes"
                                    ? "Disputed, then confirmed on a closer look."
                                    : unconfirmed
                                    ? "Seen by the page read only — the high-resolution pass did not find it. Check this one."
                                    : recovered
                                    ? "Recovered by the high-resolution pass; the page read missed it."
                                    : c.confirmedByTile
                                    ? "Confirmed by both reads."
                                    : undefined
                                }
                                className={`relative text-center px-1 py-1 border-b border-l border-white/5 ${
                                  approved ? "" : "cursor-pointer hover:bg-white/[0.06]"
                                } ${
                                  unclear
                                    ? "ring-1 ring-inset ring-amber-300 bg-amber-400/[0.12]"
                                    : unconfirmed
                                    ? "ring-1 ring-inset ring-rose-400/70 bg-rose-500/[0.07]"
                                    : low
                                    ? "ring-1 ring-inset ring-amber-400/60"
                                    : ""
                                }`}
                              >
                                {!c ? (
                                  <span className="text-slate-600">·</span>
                                ) : c.marker === "phone_icon" || c.mode === "phone" ? (
                                  <Phone className="w-3 h-3 inline text-sky-300" />
                                ) : (
                                  <span className="text-emerald-300 font-semibold">✕</span>
                                )}
                                {recovered && (
                                  <span
                                    aria-hidden="true"
                                    className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-blue-400"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
                ))}
              </section>

              {/* Footnotes */}
              {footnotes.length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                    Footnotes ({footnotes.length})
                  </h3>
                  <div className="space-y-1.5">
                    {footnotes.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <input
                          value={f.ref || ""}
                          disabled={approved}
                          onChange={(e) => setFootnote(i, "ref", e.target.value)}
                          className="w-10 bg-white/[0.04] rounded px-1.5 py-1 text-[11px] text-amber-200 outline-none shrink-0"
                        />
                        <textarea
                          value={f.text || ""}
                          disabled={approved}
                          onChange={(e) => setFootnote(i, "text", e.target.value)}
                          rows={1}
                          className="flex-1 bg-white/[0.04] rounded px-2 py-1 text-[11px] text-slate-200 outline-none resize-y"
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
